/**
 * Training queues: units with a `trainTime` are paid for when ordered
 * (`spawn`, spawn.ts) and wait in their city's `Building.training` queue.
 * At each training building (training.ts `trainingBuilding`) the first
 * `trainSlots` of its entries train at once, in queue order; the rest wait.
 * A unit done training appears as an instant spawn would (`spawnSpot`: into
 * the city's store with room, else on a free tile near the city), keeping
 * the id it was minted with when queued; with no room anywhere it waits,
 * still holding its slot, and tries again next tick.
 *
 * A queue goes with its city (nothing is refunded: the stock it was paid
 * from is plundered or lost with the city anyway). A training building that
 * falls, is no longer its owner's or no longer stands in its city's territory
 * refunds its city's entries for it in full into that city, as
 * `cancelTraining` does (so a store trains for one city only).
 */
import { getHexTileMapKey } from '../../hexmap/model/keys';
import { invalidateDerived } from '../derivedRegistry';
import { toMilli } from '../fixed';
import { type Occupancy, occupancyOf } from '../occupancy';
import { mintUnitId } from '../state';
import { deposit } from '../stock';
import { territoryOwners } from '../territory';
import { trainSlots, trainTime } from '../training';
import type { Building, EntityId, SimState, SystemFn, Training } from '../types';
import { addSpawnedUnit, spawnSpot } from './spawn';

/** Puts `paid` (thousandths) back into city `cityId`'s stock. */
function refundTraining(state: SimState, cityId: EntityId, t: Training): void {
  for (const [res, amount] of Object.entries(t.paid)) deposit(state, cityId, res, amount);
}

/**
 * Takes entry `trainingId` out of `playerId`'s city `cityId`'s queue and
 * refunds it in full (the `cancelTraining` command). False when there's no
 * such entry of theirs.
 */
function cancelTraining(
  state: SimState,
  playerId: string,
  cityId: EntityId,
  trainingId: EntityId
): boolean {
  const city = state.buildings[cityId];
  const queue = city?.owner === playerId ? city.training : undefined;
  const i = queue?.findIndex((t) => t.id === trainingId) ?? -1;
  if (!city || !queue || i < 0) return false;
  const [t] = queue.splice(i, 1);
  if (queue.length === 0) delete city.training;
  refundTraining(state, cityId, t);
  return true;
}

const training: SystemFn = (state, ctx, _inputs, dt) => {
  const { rules } = ctx;
  const step = toMilli(dt);
  let occ: Occupancy | undefined;
  const owners = territoryOwners(state, rules);
  for (const city of Object.values(state.buildings)) {
    if (!city.training) continue;
    // A training building gone (or now another city's): its units are refunded.
    const queue: Training[] = [];
    for (const t of city.training) {
      const at: Building | undefined = state.buildings[t.at];
      if (
        at &&
        at.owner === city.owner &&
        trainSlots(rules, at) > 0 &&
        owners.get(getHexTileMapKey(at.pos)) === city.id
      )
        queue.push(t);
      else refundTraining(state, city.id, t);
    }
    const used = new Map<EntityId, number>();
    const done: Training[] = [];
    for (const t of queue) {
      const n = used.get(t.at) ?? 0;
      if (n >= trainSlots(rules, state.buildings[t.at])) continue;
      used.set(t.at, n + 1);
      const time = trainTime(rules, t.unitType);
      t.progress = Math.min(time, t.progress + step);
      if (t.progress >= time) done.push(t);
    }
    for (const t of done) {
      occ ??= occupancyOf(state, rules);
      const spot = spawnSpot(state, ctx, occ, city, t.unitType);
      if (!spot) continue;
      // Ids are minted when queued; one taken since (a hand-made snapshot) gets a fresh one.
      const id = t.id in state.units ? mintUnitId(state) : t.id;
      addSpawnedUnit(state, ctx, city, t.unitType, spot, id);
      queue.splice(queue.indexOf(t), 1);
      occ = undefined;
      invalidateDerived(state);
    }
    if (queue.length > 0) city.training = queue;
    else delete city.training;
  }
};

export { cancelTraining, training };
